// Formatting for a booking's load snapshot, shared by the price page.

// Whole dollars stay whole; a rate with cents shows both places.
export function dollars(n) {
  const v = Number(n);
  const whole = Math.round(v * 100) % 100 === 0;
  return `$${v.toLocaleString("en-US", {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  })}`;
}

function place(city, state) {
  const c = (city || "").trim();
  const s = (state || "").trim();
  return !c && !s ? "—" : !c ? s : !s ? c : `${c}, ${s}`;
}

// Appointment times, when the board sends them. CH stamps a `Z` on them but
// the digits are the dock's own clock — the rate con for load 569036523 says
// "Pick Up Time: 12:00" for what search sent as 12:00Z. So the
// digits are shown as sent, never converted, with no zone label.
//
// Boards that only publish a date send midnight, which is a placeholder and
// not an appointment, so it stays a bare date.
function wall(iso) {
  // Date.UTC + timeZone "UTC" below round-trips the digits untouched.
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(iso || ""));
  return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5])) : null;
}

const asSent = { timeZone: "UTC" };
const dayOf = (d) =>
  d.toLocaleDateString("en-US", { month: "short", day: "numeric", ...asSent });
const timeOf = (d) =>
  d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    ...asSent,
  });

export function appointment(iso, endIso) {
  const at = wall(iso);
  if (!at) return null;
  if (at.getUTCHours() === 0 && at.getUTCMinutes() === 0) return dayOf(at);
  const end = wall(endIso);
  if (end && end > at) {
    return dayOf(at) === dayOf(end)
      ? `${dayOf(at)}, ${timeOf(at)} - ${timeOf(end)}`
      : `${dayOf(at)}, ${timeOf(at)} - ${dayOf(end)}, ${timeOf(end)}`;
  }
  return `${dayOf(at)}, ${timeOf(at)}`;
}

// CH lists some fixed appointments as a one-minute window (17:00-17:01).
function stopAppointment(stop) {
  const start = wall(stop.arrive_start);
  const end = wall(stop.arrive_end);
  const isWindow = start && end && end - start > 60 * 1000;
  return appointment(stop.arrive_start, isWindow ? stop.arrive_end : null);
}

// Dock hours as listed ("08:00"), on the dock's own clock.
function dockHours(open, close) {
  const hm = (v) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(String(v || ""));
    return m && +m[1] <= 23 && +m[2] <= 59 ? [+m[1], +m[2]] : null;
  };
  const o = hm(open);
  const c = hm(close);
  if (!o || !c) return null;
  const same = o[0] === c[0] && o[1] === c[1];
  if (same && (o[0] || o[1])) return null;
  // Same rule as the backend's _dock_hours, so the page and the agent agree.
  const allDay =
    o[0] === 0 &&
    o[1] <= 1 &&
    ((c[0] === 23 && c[1] === 59) || (c[0] === 0 && c[1] === 0));
  if (allDay) return "Open 24 hours";
  const clock = ([h, m]) =>
    `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
  return `${clock(o)} - ${clock(c)}`;
}

const countLabel = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

// Route, stops and facts for the price page's load details.
export function describeLoad(load) {
  const l = load || {};
  const origin = place(l.oc, l.os);
  const dest = place(l.dc, l.ds);
  const pay = l.py != null ? Number(l.py) : null;
  const miles = l.di ? Number(l.di) : null;
  const rpm =
    l.rpm != null ? Number(l.rpm) : pay != null && miles ? pay / miles : null;

  const listed = Array.isArray(l.stp)
    ? l.stp.filter((s) => s && (s.type === "PICK" || s.type === "DROP"))
    : [];
  const picks = listed.filter((s) => s.type === "PICK");
  const drops = listed.filter((s) => s.type === "DROP");
  const multiStop = picks.length > 1 || drops.length > 1;

  const stops = multiStop
    ? listed.map((s) => {
        const pick = s.type === "PICK";
        const same = pick ? picks : drops;
        const name = pick ? "Pickup" : "Drop";
        const cityState = [s.city, s.state].filter(Boolean).join(", ");
        const details = [
          dockHours(s.open_time, s.close_time) &&
            `Dock hours ${dockHours(s.open_time, s.close_time)}`,
          s.weight != null && `Weight ${Number(s.weight).toLocaleString()} lbs`,
        ]
          .filter(Boolean)
          .join(" · ");
        return {
          pick,
          label: same.length > 1 ? `${name} ${same.indexOf(s) + 1}` : name,
          place: [cityState, s.postal].filter(Boolean).join(" ") || "—",
          when: stopAppointment(s),
          details,
        };
      })
    : [
        {
          pick: true,
          label: "Pickup",
          place: origin,
          when: appointment(l.pu, l.ple) || l.sd || null,
        },
        {
          pick: false,
          label: "Drop",
          place: dest,
          when: appointment(l.dl, l.dle),
        },
      ];

  const facts = [
    rpm != null && { label: "Rate per mile", value: `$${rpm.toFixed(2)}` },
    miles != null && { label: "Miles", value: `${miles.toLocaleString()} mi` },
    l.dh != null && {
      label: "Deadhead",
      value: `${Number(l.dh).toLocaleString()} mi`,
    },
    l.wt != null && {
      label: "Weight",
      value: `${Number(l.wt).toLocaleString()} lbs`,
    },
    l.ln != null && { label: "Length", value: `${Number(l.ln)} ft` },
    l.eq && { label: "Equipment", value: l.eq },
  ].filter(Boolean);

  return {
    origin,
    dest,
    stops,
    facts,
    stopSummary: multiStop
      ? l.ss ||
        `${countLabel(picks.length, "pick")}, ${countLabel(drops.length, "drop")}`
      : null,
  };
}
